import { useState, useEffect, useCallback, useRef } from "react";
import { sGet, sPut, sPost } from "../../utils/staffApi";
import {
  C,
  Input,
  Select,
  Btn,
  Table,
  Pagination,
  Modal,
  Badge,
  EmptyState,
  LoadingSpinner,
} from "./AdminPanel";
import "../../styles/orders.css";

// React Icons imports
import {
  FiSearch,
  FiEye,
  FiEdit2,
  FiPackage,
  FiTruck,
  FiCalendar,
  FiCreditCard,
  FiMapPin,
  FiMail,
  FiUser,
  FiFilter,
  FiX,
  FiPrinter,
  FiShare2,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
  FiSend,
} from "react-icons/fi";
import {
  FaRupeeSign,
  FaBoxOpen,
  FaShippingFast,
  FaWhatsapp,
  FaEnvelope,
  FaRegClock,
} from "react-icons/fa";
import {
  MdTrackChanges,
  MdPhone,
  MdLocationOn,
  MdPerson,
  MdTimeline,
  MdLocalShipping,
  MdPayment,
} from "react-icons/md";

const ORDER_STATUSES = [
  {
    value: "Processing",
    label: "Processing",
    color: "#f59e0b",
    icon: FiLoader,
  },
  {
    value: "Confirmed",
    label: "Confirmed",
    color: "#6366f1",
    icon: FiCheckCircle,
  },
  { value: "Shipped", label: "Shipped", color: "#06b6d4", icon: FiTruck },
  {
    value: "Out for Delivery",
    label: "Out for Delivery",
    color: "#f97316",
    icon: FaShippingFast,
  },
  {
    value: "Delivered",
    label: "Delivered",
    color: "#10b981",
    icon: FiCheckCircle,
  },
  {
    value: "Cancelled",
    label: "Cancelled",
    color: "#ef4444",
    icon: FiAlertCircle,
  },
];

const STATUS_FLOW = [
  "Processing",
  "Confirmed",
  "Shipped",
  "Out for Delivery",
  "Delivered",
];

// Timeline component
function OrderTimeline({ currentStatus, createdAt, updatedAt }) {
  const getStatusIndex = (status) => STATUS_FLOW.indexOf(status);
  const currentIndex = getStatusIndex(currentStatus);

  return (
    <div className="order-timeline-enhanced">
      <div className="timeline-track">
        {STATUS_FLOW.map((status, idx) => {
          const isCompleted = idx <= currentIndex;
          const statusMeta = ORDER_STATUSES.find((s) => s.value === status);
          const Icon = statusMeta?.icon;
          return (
            <div
              key={status}
              className={`timeline-step ${isCompleted ? "completed" : "pending"}`}
            >
              <div className="timeline-dot">
                {isCompleted ? <FiCheckCircle /> : <Icon size={12} />}
              </div>
              <div className="timeline-line" />
              <div className="timeline-label">{status}</div>
            </div>
          );
        })}
      </div>
      <div className="timeline-footer">
        <span className="timeline-date">
          <FiCalendar size={12} /> Ordered:{" "}
          {new Date(createdAt).toLocaleDateString("en-IN")}
        </span>
        {updatedAt && updatedAt !== createdAt && (
          <span className="timeline-date">
            <FiClock size={12} /> Last updated:{" "}
            {new Date(updatedAt).toLocaleDateString("en-IN")}
          </span>
        )}
      </div>
    </div>
  );
}

// Status badge with icon
function StatusBadgeEnhanced({ status }) {
  const meta =
    ORDER_STATUSES.find((s) => s.value === status) || ORDER_STATUSES[0];
  const Icon = meta.icon;
  return (
    <div
      className="status-badge-enhanced"
      style={{ background: `${meta.color}15`, color: meta.color }}
    >
      <Icon size={12} />
      <span>{status}</span>
    </div>
  );
}

// Order Card for mobile view
function OrderCardMobile({ order, onView, onUpdate, onSendInvoice }) {
  return (
    <div className="order-card-mobile">
      <div className="order-card-header">
        <div className="order-id">#{order.orderId}</div>
        <StatusBadgeEnhanced status={order.orderStatus} />
      </div>
      <div className="order-card-body">
        <div className="order-customer">
          <FiUser size={14} />
          <span>{order.user?.name || "Guest"}</span>
        </div>
        <div className="order-amount">
          <FaRupeeSign size={12} />
          <span>₹{order.totalAmount?.toLocaleString()}</span>
        </div>
        <div className="order-items-count">
          <FaBoxOpen size={12} />
          <span>{order.items?.length || 0} items</span>
        </div>
        <div className="order-date">
          <FiCalendar size={12} />
          <span>{new Date(order.createdAt).toLocaleDateString("en-IN")}</span>
        </div>
      </div>
      <div className="order-card-actions">
        <Btn
          size="sm"
          variant="ghost"
          onClick={() => onView(order.id)}
          icon={<FiEye size={14} />}
        >
          View
        </Btn>
        <Btn
          size="sm"
          variant="secondary"
          onClick={() => onUpdate(order)}
          icon={<FiEdit2 size={14} />}
        >
          Update
        </Btn>
        <Btn
          size="sm"
          variant="primary"
          onClick={() => onSendInvoice(order)}
          icon={<FiSend size={14} />}
        >
          Invoice
        </Btn>
      </div>
    </div>
  );
}

export default function Orders({ toast }) {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [updModal, setUpdModal] = useState(null);
  const [updForm, setUpdForm] = useState({
    status: "",
    trackingNumber: "",
    courierPartner: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const debounceRef = useRef();

  const sendInvoice = async (order) => {
    setSendingInvoice(order.id);
    try {
      const token = localStorage.getItem("kkb_token");
      const res = await fetch(
        `https://lightslategrey-mole-360881.hostingersite.com/api/orders/${order.id}/invoice`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      toast("Invoice sent successfully to customer email!");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSendingInvoice(null);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    sGet("/orders", { page, search, status })
      .then((d) => {
        setOrders(d.orders);
        setTotal(d.total);
        setPages(d.pages);
      })
      .catch((e) => toast(e.message, "error"))
      .finally(() => setLoading(false));
  }, [page, search, status, toast]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 400);
  }, [load]);

  const openUpdate = (order) => {
    setUpdForm({
      status: order.orderStatus,
      trackingNumber: order.trackingNumber || "",
      courierPartner: order.courierPartner || "",
      notes: order.notes || "",
    });
    setUpdModal(order);
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("kkb_token");
      const updatePayload = {
        orderStatus: updForm.status,
        trackingNumber: updForm.trackingNumber,
        courierPartner: updForm.courierPartner,
        notes: updForm.notes,
      };

      const res = await fetch(
        `https://lightslategrey-mole-360881.hostingersite.com/api/orders/${updModal.id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify(updatePayload),
        },
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update order");

      toast(data.message || "Order updated successfully!");
      setUpdModal(null);

      if (selected && selected.id === updModal.id) {
        await viewOrder(selected.id);
      }
      await load();
    } catch (e) {
      console.error("Update error:", e);
      toast(e.message || "Failed to update order", "error");
    } finally {
      setSaving(false);
    }
  };

  const viewOrder = async (id) => {
    try {
      const order = await sGet(`/orders/${id}`);
      setSelected(order);
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setPage(1);
  };

  const getStatusCount = () => {
    const counts = {};
    orders.forEach((order) => {
      counts[order.orderStatus] = (counts[order.orderStatus] || 0) + 1;
    });
    return counts;
  };

  const statusCounts = getStatusCount();

  return (
    <div className="orders-container-enhanced">
      {/* Header */}
      <div className="orders-header-enhanced">
        <div>
          <h1 className="orders-title">Order Management</h1>
          <p className="orders-subtitle">
            Track, manage, and update customer orders
          </p>
        </div>
        <div className="orders-stats-badge">
          <FiPackage size={18} />
          <span>{total} Total Orders</span>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="status-tabs-enhanced">
        <button
          className={`status-tab ${!status ? "active" : ""}`}
          onClick={() => {
            setStatus("");
            setPage(1);
          }}
        >
          All Orders
          <span className="tab-count">{total}</span>
        </button>
        {ORDER_STATUSES.map((s) => (
          <button
            key={s.value}
            className={`status-tab ${status === s.value ? "active" : ""}`}
            onClick={() => {
              setStatus(s.value);
              setPage(1);
            }}
          >
            <span className="tab-dot" style={{ background: s.color }} />
            {s.label}
            <span className="tab-count">{statusCounts[s.value] || 0}</span>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="orders-toolbar-enhanced">
        <div className="toolbar-left-enhanced">
          <div className="search-wrapper-enhanced">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by order ID, customer name, or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="search-input-enhanced"
            />
            {search && (
              <button onClick={() => setSearch("")} className="search-clear">
                <FiX />
              </button>
            )}
          </div>
          <div className="filter-wrapper">
            <button
              className={`filter-trigger ${filterOpen ? "active" : ""}`}
              onClick={() => setFilterOpen(!filterOpen)}
            >
              <FiFilter /> Filter
            </button>
          </div>
        </div>
        <div className="toolbar-right-enhanced">
          {(search || status) && (
            <button onClick={clearFilters} className="clear-filters-btn">
              <FiX /> Clear filters
            </button>
          )}
          <span className="orders-count">
            Showing {orders.length} of {total} orders
          </span>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="orders-table-wrapper-enhanced">
        <Table
          loading={loading}
          columns={[
            {
              key: "orderId",
              label: "Order ID",
              width: "120px",
              render: (v) => (
                <div className="order-id-cell">
                  <FiPackage className="cell-icon" />
                  <span className="order-id-text">#{v}</span>
                </div>
              ),
            },
            {
              key: "user",
              label: "Customer",
              render: (v, row) => (
                <div className="customer-cell">
                  <div className="customer-avatar">
                    {(v?.name || row.shippingAddress?.firstName || "G")
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                  <div className="customer-info">
                    <div className="customer-name">{v?.name || "Guest"}</div>
                    <div className="customer-email">
                      <FiMail size={12} />{" "}
                      {row.shippingAddress?.email || "No email"}
                    </div>
                  </div>
                </div>
              ),
            },
            {
              key: "totalAmount",
              label: "Amount",
              render: (v) => (
                <div className="amount-cell">
                  <FaRupeeSign className="currency-icon" />
                  <span className="amount-value">{v?.toLocaleString()}</span>
                </div>
              ),
            },
            {
              key: "items",
              label: "Items",
              render: (v) => (
                <div className="items-cell">
                  <FaBoxOpen />
                  <span>{v?.length || 0}</span>
                </div>
              ),
            },
            // ✅ NEW COLUMN: Display Colors
            {
              key: "colors",
              label: "Colors",
              width: "150px",
              render: (_, row) => {
                // Parse items if it's a string, otherwise use as is
                let items = row.items;
                if (typeof items === "string") {
                  try {
                    items = JSON.parse(items);
                  } catch (e) {
                    items = [];
                  }
                }

                // Ensure items is an array
                if (!Array.isArray(items)) {
                  items = [];
                }

                // Extract unique colors from items
                const uniqueColors = [
                  ...new Set(
                    items
                      .map((item) => item.color)
                      .filter((color) => color && color !== "default"),
                  ),
                ];

                if (uniqueColors.length === 0) {
                  return <span className="no-colors">—</span>;
                }

                return (
                  <div className="colors-cell">
                    {uniqueColors.map((color, idx) => (
                      <span key={idx} className="color-badge">
                        {color}
                      </span>
                    ))}
                  </div>
                );
              },
            },
            {
              key: "paymentMethod",
              label: "Payment",
              render: (v) => (
                <div className="payment-cell">
                  <MdPayment />
                  <span>{v?.toUpperCase()}</span>
                </div>
              ),
            },
            {
              key: "orderStatus",
              label: "Status",
              render: (v) => <StatusBadgeEnhanced status={v} />,
            },
            {
              key: "createdAt",
              label: "Date",
              render: (v) => (
                <div className="date-cell">
                  <FiCalendar />
                  <span>{new Date(v).toLocaleDateString("en-IN")}</span>
                </div>
              ),
            },
            {
              key: "id",
              label: "Actions",
              width: "180px",
              render: (v, row) => (
                <div className="action-buttons">
                  <button
                    className="action-icon"
                    onClick={() => viewOrder(v)}
                    title="View Details"
                  >
                    <FiEye />
                  </button>
                  <button
                    className="action-icon"
                    onClick={() => openUpdate(row)}
                    title="Update Status"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="action-icon invoice"
                    onClick={() => sendInvoice(row)}
                    title="Send Invoice"
                    disabled={sendingInvoice === row.id}
                  >
                    {sendingInvoice === row.id ? (
                      <FiLoader className="spin" />
                    ) : (
                      <FiSend />
                    )}
                  </button>
                </div>
              ),
            },
          ]}
          data={orders}
          rowKey="id"
        />
      </div>

      {/* Mobile Card View */}
      {!loading && orders.length > 0 && (
        <div className="orders-mobile-view">
          {orders.map((order) => (
            <OrderCardMobile
              key={order.id}
              order={order}
              onView={viewOrder}
              onUpdate={openUpdate}
              onSendInvoice={sendInvoice}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && orders.length === 0 && (
        <EmptyState
          icon={FiPackage}
          title="No orders found"
          description="Try adjusting your search or filter criteria"
          action={
            search || status
              ? { label: "Clear filters", onClick: clearFilters }
              : null
          }
        />
      )}

      {/* Loading State */}
      {loading && (
        <div className="orders-loading">
          <LoadingSpinner size="md" text="Loading orders..." />
        </div>
      )}

      {/* Pagination */}
      {!loading && orders.length > 0 && (
        <Pagination page={page} pages={pages} onChange={setPage} />
      )}

      {/* View Order Modal - Enhanced */}
      {selected && (
        <Modal
          title={`Order Details - #${selected.orderId}`}
          onClose={() => setSelected(null)}
          size="lg"
        >
          <div className="order-view-enhanced">
            {/* Header with status */}
            <div className="order-view-header">
              <div className="order-view-status">
                <StatusBadgeEnhanced status={selected.orderStatus} />
                <span className="order-view-date">
                  <FiCalendar />{" "}
                  {new Date(selected.createdAt).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="order-view-actions">
                <button
                  onClick={() => sendInvoice(selected)}
                  className="view-action-btn"
                >
                  <FiSend /> Send Invoice
                </button>
              </div>
            </div>

            {/* Timeline */}
            <OrderTimeline
              currentStatus={selected.orderStatus}
              createdAt={selected.createdAt}
              updatedAt={selected.updatedAt}
            />

            {/* Customer & Shipping Info Grid */}
            <div className="info-grid-enhanced">
              <div className="info-card">
                <div className="info-card-header">
                  <FiUser /> Customer Information
                </div>
                <div className="info-card-content">
                  <p>
                    <strong>Name:</strong> {selected.shippingAddress?.firstName}{" "}
                    {selected.shippingAddress?.lastName}
                  </p>
                  <p>
                    <strong>Email:</strong> <FiMail size={12} />{" "}
                    {selected.shippingAddress?.email}
                  </p>
                  <p>
                    <strong>Phone:</strong> <MdPhone size={12} />{" "}
                    {selected.shippingAddress?.phone}
                  </p>
                </div>
              </div>
              <div className="info-card">
                <div className="info-card-header">
                  <MdLocationOn /> Shipping Address
                </div>
                <div className="info-card-content">
                  <p>{selected.shippingAddress?.address}</p>
                  <p>
                    {selected.shippingAddress?.city},{" "}
                    {selected.shippingAddress?.state}
                  </p>
                  <p>Pincode: {selected.shippingAddress?.pincode}</p>
                </div>
              </div>
              <div className="info-card">
                <div className="info-card-header">
                  <FiCreditCard /> Payment Details
                </div>
                <div className="info-card-content">
                  <p>
                    <strong>Method:</strong>{" "}
                    {selected.paymentMethod?.toUpperCase()}
                  </p>
                  <p>
                    <strong>Total:</strong> <FaRupeeSign /> ₹
                    {selected.totalAmount?.toLocaleString()}
                  </p>
                  {selected.paymentId && (
                    <p>
                      <strong>Payment ID:</strong> {selected.paymentId}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Tracking Info (if available) */}
            {(selected.trackingNumber || selected.courierPartner) && (
              <div className="tracking-info-card">
                <div className="info-card-header">
                  <FaShippingFast /> Tracking Information
                </div>
                <div className="tracking-details">
                  <div className="tracking-item">
                    <span className="tracking-label">Tracking Number:</span>
                    <span className="tracking-value">
                      {selected.trackingNumber}
                    </span>
                  </div>
                  <div className="tracking-item">
                    <span className="tracking-label">Courier Partner:</span>
                    <span className="tracking-value">
                      {selected.courierPartner}
                    </span>
                  </div>
                  <div className="tracking-item">
                    <span className="tracking-label">Track URL:</span>
                    <a href="#" className="tracking-link">
                      Track shipment →
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Items List */}
            <div className="items-list-enhanced">
              <div className="info-card-header">
                <FaBoxOpen /> Order Items ({selected.items?.length})
              </div>
              <div className="items-table">
                <div className="items-header">
                  <span>Product</span>
                  <span>Size</span>
                  <span>Color</span> {/* Add Color column */}
                  <span>Qty</span>
                  <span>Price</span>
                  <span>Total</span>
                </div>
                {selected.items?.map((item, i) => (
                  <div key={i} className="items-row">
                    <span className="item-name">{item.name}</span>
                    <span className="item-size">{item.size || "-"}</span>
                    <span className="item-color">{item.color || "-"}</span>{" "}
                    {/* Add Color value */}
                    <span className="item-qty">{item.qty}</span>
                    <span className="item-price">
                      ₹{item.price?.toLocaleString()}
                    </span>
                    <span className="item-total">
                      ₹{(item.price * item.qty)?.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
              <div className="items-summary">
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>
                    ₹
                    {selected.subtotal?.toLocaleString() ||
                      selected.totalAmount?.toLocaleString()}
                  </span>
                </div>
                {selected.shippingCharge > 0 && (
                  <div className="summary-row">
                    <span>Shipping:</span>
                    <span>₹{selected.shippingCharge?.toLocaleString()}</span>
                  </div>
                )}
                {selected.discount > 0 && (
                  <div className="summary-row discount">
                    <span>Discount:</span>
                    <span>-₹{selected.discount?.toLocaleString()}</span>
                  </div>
                )}
                <div className="summary-row total">
                  <span>Total:</span>
                  <span>₹{selected.totalAmount?.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="modal-footer-actions">
              <Btn variant="ghost" onClick={() => setSelected(null)}>
                Close
              </Btn>
              <Btn
                variant="primary"
                onClick={() => {
                  setSelected(null);
                  openUpdate(selected);
                }}
              >
                <FiEdit2 /> Update Status
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Update Status Modal - Enhanced */}
      {updModal && (
        <Modal
          title={`Update Order - #${updModal.orderId}`}
          onClose={() => setUpdModal(null)}
          size="md"
        >
          <div className="update-order-enhanced">
            <div className="current-status-info">
              <span className="label">Current Status:</span>
              <StatusBadgeEnhanced status={updModal.orderStatus} />
            </div>

            <Select
              label="New Status"
              value={updForm.status}
              onChange={(e) =>
                setUpdForm((p) => ({ ...p, status: e.target.value }))
              }
            >
              {ORDER_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>

            <div className="form-row">
              <Input
                label="Tracking Number"
                placeholder="Enter tracking number"
                value={updForm.trackingNumber}
                onChange={(e) =>
                  setUpdForm((p) => ({ ...p, trackingNumber: e.target.value }))
                }
                icon={<FiTruck />}
              />
              <Input
                label="Courier Partner"
                placeholder="e.g., Delhivery, Blue Dart"
                value={updForm.courierPartner}
                onChange={(e) =>
                  setUpdForm((p) => ({ ...p, courierPartner: e.target.value }))
                }
                icon={<FaShippingFast />}
              />
            </div>

            <div className="form-group">
              <label className="input-label-enhanced">Internal Notes</label>
              <textarea
                className="notes-textarea"
                placeholder="Add internal notes about this order..."
                value={updForm.notes}
                onChange={(e) =>
                  setUpdForm((p) => ({ ...p, notes: e.target.value }))
                }
                rows={3}
              />
            </div>

            <div className="whatsapp-notice">
              <FaWhatsapp color="#25D366" />
              <span>
                Customer will receive a WhatsApp notification with updated order
                status
              </span>
            </div>

            <div className="modal-actions-enhanced">
              <Btn
                variant="ghost"
                onClick={() => setUpdModal(null)}
                disabled={saving}
              >
                Cancel
              </Btn>
              <Btn
                variant="primary"
                onClick={handleUpdate}
                disabled={saving}
                loading={saving}
              >
                {saving ? "Updating..." : "Update Order"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
